import { useEffect, useState, useCallback } from "react";
import { Loader2, Heart, Wallet, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import ExportMenu from "@/components/common/ExportMenu";

function SummaryStat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-amtm-textMuted text-xs mb-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="font-display text-xl text-amtm-text">{value}</p>
    </div>
  );
}

const STATUS_LABELS = {
  active: "Active", created: "Created", authenticated: "Authenticated", pending: "Pending",
  halted: "Needs Attention", cancelled: "Cancelled", completed: "Completed",
  one_time: "One-time donor", no_payments: "No payments yet",
};

const money = (v) => (v || v === 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "—");

export default function GuardianDonorsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchDonors = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/guardian/admin/donors", { params: { search: search || undefined, limit: 100 } });
    setItems(data.items);
    setTotal(data.total);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchDonors, 300);
    return () => clearTimeout(t);
  }, [fetchDonors]);

  useEffect(() => {
    api.get("/guardian/admin/dashboard").then(({ data }) => setSummary(data));
  }, []);

  return (
    <div data-testid="guardian-donors-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryStat icon={Users} label="Total Donors" value={summary.total_donors} testId="guardian-donors-summary-total" />
          <SummaryStat icon={Wallet} label="Total Raised (lifetime)" value={money(summary.total_raised_lifetime)} testId="guardian-donors-summary-raised" />
          <SummaryStat icon={Heart} label="Active Guardians" value={summary.active_guardians} testId="guardian-donors-summary-active" />
          <SummaryStat icon={Wallet} label="MRR" value={money(summary.mrr)} testId="guardian-donors-summary-mrr" />
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <Input
          placeholder="Search by name, email, or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="guardian-donor-search-input"
          className="h-10 w-64"
        />
        <ExportMenu url="/guardian/admin/donors/export" params={{ search: search || undefined }} filename="amtm_guardian_donors" testId="guardian-donor-export" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      ) : items.length === 0 ? (
        <p className="text-amtm-textMuted text-center py-12" data-testid="guardian-donors-empty">No donors found.</p>
      ) : (
        <div className="bg-amtm-surface border border-amtm-border rounded-xl overflow-x-auto" data-testid="guardian-donors-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-amtm-textMuted border-b border-amtm-border">
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Contact</th>
                <th className="p-3 font-medium">Monthly</th>
                <th className="p-3 font-medium">Total Given</th>
                <th className="p-3 font-medium">Payments</th>
                <th className="p-3 font-medium">Started On</th>
                <th className="p-3 font-medium">Next Charge</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amtm-border">
              {items.map((d) => (
                <tr key={d.id} data-testid={`guardian-donor-row-${d.id}`}>
                  <td className="p-3 font-semibold text-amtm-text">{d.name}</td>
                  <td className="p-3 text-amtm-textMuted">
                    {d.email}
                    <br />
                    {d.phone}
                  </td>
                  <td className="p-3">{d.monthly_amount ? `${money(d.monthly_amount)}/mo` : "—"}</td>
                  <td className="p-3 font-semibold text-amtm-success">{money(d.total_given)}</td>
                  <td className="p-3">{d.payments_count}</td>
                  <td className="p-3">{formatDate(d.created_at)}</td>
                  <td className="p-3">{d.next_charge_at ? formatDate(d.next_charge_at) : "—"}</td>
                  <td className="p-3"><Badge variant="outline">{STATUS_LABELS[d.subscription_status] || d.subscription_status}</Badge></td>
                  <td className="p-3">{d.source === "direct" ? "Direct" : d.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <p className="text-xs text-amtm-textMuted mt-2">{total} total donor(s)</p>}
    </div>
  );
}
